import { BaseFields, BaseFieldInstance, InferFieldsValue } from '../fields/types';
import { BaseValidationError } from '../types';

import { $internals, BaseFormActions, FormActionsMap, FormDeclaration } from './types';
import { $internals as $fieldInternals } from '../fields/types';
import { useSubscription } from './useSubscription';
import { FormState } from '../store/types';

/**
 * Параметры хука `useFormState` для получения состояния формы
 *
 * Используется для получения состояния формы с точными типами ошибок валидации.
 * Рекомендуется использовать этот способ для получения состояния формы в компонентах.
 */
type UseFormStateOptions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
> = {
    /** Декларация формы, состояние которой нужно получить */
    form: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    /** Уникальный идентификатор экземпляра формы */
    id: string;
};

/**
 * Возвращает общее состояние формы
 *
 * @param options - Объект с декларацией формы и идентификатором
 * @returns Состояние формы с точными типами ошибок валидации и initialValue
 *
 * @example
 * ```tsx
 * const formState = useFormState({ form, id: 'form-id' });
 * console.log(formState.isSubmitting); // boolean
 * console.log(formState.errors); // Точные типы ошибок формы
 * console.log(formState.initialValue); // { foo: string }
 * ```
 */
export function useFormState<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    options: UseFormStateOptions<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >
): FormState<InferFieldsValue<FormFields>, FormValidationError>;

/**
 * Возвращает состояние формы для конкретного поля
 *
 * @param field - Инстанс поля формы
 * @returns Состояние формы с общими типами ошибок валидации и initialValue - unknown
 *
 * @example
 * ```tsx
 * const field = useField(fields.someField);
 * const formState = useFormState(field);
 * // или напрямую:
 * const formState = useFormState(fields.someField);
 * console.log(formState.isSubmitting); // boolean
 * console.log(formState.errors); // BaseValidationError[]
 * console.log(formState.initialValue); // unknown
 * ```
 */
export function useFormState(field: {
    form: BaseFieldInstance['form'];
    [$fieldInternals]: unknown;
}): FormState<unknown, BaseValidationError>;

/**
 * Реализация хука useFormState
 *
 * Поддерживает два способа вызова:
 * 1. С объектом опций { form, id } - возвращает стейт формы с точными типами ошибок
 * 2. С инстансом поля - возвращает стейт формы с общими типами ошибок
 */
export function useFormState(
    formStateOption:
        | UseFormStateOptions<
              string,
              BaseFields,
              BaseValidationError,
              unknown,
              unknown,
              BaseFormActions,
              unknown,
              unknown
          >
        | { form: BaseFieldInstance['form']; [$fieldInternals]: unknown }
): FormState<unknown, BaseValidationError> {
    const formInstance =
        $fieldInternals in formStateOption
            ? formStateOption.form
            : formStateOption.form.getInstance(formStateOption.id);

    useSubscription(formInstance);

    return formInstance[$internals].useState();
}
